<template>
  <div class="select-popover" :class="`${type}-select`">
    <PopoverMenu
      size="fit-content"
      trigger="click"
      placement="bottom-start"
      ref="Popover"
      @hide="onPopoverHide"
      @auto-hide="onPopoverAutoHide"
      auto-hide
      v-model:open="open"
      :focus-on-show="!search"
      :data-test-id="`select-popover-${label}`"
    >
      <template #trigger>
        <Btn
          :color="buttonColor"
          :size="buttonSize"
          :icon="buttonIcon"
          class="select-popover__button"
          layout="space-between"
          :class="{ 'select-popover__button--error': error }"
          :data-test-id="`${type}-select-toggle`"
          :data-test-select-toggle="label"
          :disabled="isDisabled"
        >
          <slot name="button-content" />
        </Btn>
      </template>

      <template #items-list>
        <PopoverMenuSection
          class="select-popover__wrapper"
          :class="{ 'select-popover__wrapper-no-search': !search }"
          :title="menuTitle"
        >
          <div :data-test-id="`${type}-select`" :data-test-select="label" class="select-popover-content">
            <ListController
              @action="dispatchToSelectionList"
              :start-highlight-on-focus-in="initiallyHighlightFirstElement"
              v-if="search"
              class="select-popover__search"
            >
              <InputBox v-model="query" focus-on-mount :placeholder="placeholder" icon="24_Search" ref="input">
                <template #icon>
                  <SearchIcon16 />
                </template>
              </InputBox>
            </ListController>
            <div class="select-popover__selection-list">
              <SelectionList
                v-slot="props"
                :focus-margin="false"
                :options="filteredOptions"
                :groups="optionGroups"
                v-model="currentValue"
                :multi="type === 'multi'"
                multi-style="CheckBoxToggle"
                :clickable-option="clickableOption"
                :empty-text="!filteredOptions.length && query ? searchEmptyText : emptyText"
                density="dense"
                :max-size="size === 'fixed' ? '500px' : null"
                track-options-to-render
                @update:model-value="onValueChange"
                ref="SelectionList"
                :class="`select-popover__list select-popover__list--size-${size}`"
              >
                <!-- if custom control is specified, render a placeholder if item is not visible.
                      SelectPopover should always use items that are the same size as the placeholder
                -->
                <SelectItemPlaceholder v-if="clickableOption === 'custom-control' && !props.shouldRender" />
                <slot v-else v-bind="props" />
              </SelectionList>
            </div>
          </div>
        </PopoverMenuSection>

        <template v-if="showActionButtons && (type === 'multi' || mode === 'apply')">
          <PopoverMenuSection divider data-test-id="select-popover__controls">
            <template v-if="mode === 'apply'">
              <Btn v-close-popper @click="apply" data-test-id="select-popover__apply-button">
                {{ $t('action.apply') }}
              </Btn>
              <Btn color="grey" v-close-popper @click="cancel" data-test-id="select-popover__cancel-button">
                {{ $t('action.cancel') }}
              </Btn>
            </template>
            <Btn
              v-else-if="type === 'multi'"
              v-close-popper
              @click="$emit('done')"
              data-test-id="select-popover__done-button"
            >
              {{ $t('action.done') }}
            </Btn>
          </PopoverMenuSection>
        </template>
      </template>
    </PopoverMenu>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { filter, prop, toLower } from 'ramda';
import SearchIcon16 from '@components/Icons/SearchIcon16.vue';
import SelectItemPlaceholder from '@components/SelectItemPlaceholder.vue';
import type { ListGroups } from './SelectionList.vue';
import { OverlayCloseEvent } from './Overlay.vue';

/**
 * A single / multi select dropdown component
 *
 * - two modes, "change" and "apply"
 * - exact-match search field in the dropdown.
 * - custom option elements via default slot
 * - custom content for the opener button via slot button-content
 *
 * Uses PopoverMenu internally.
 *
 * # Change Mode
 *
 * This is the default. Whenever the selection
 * changes, the components emits "change" and the parent
 * component has to update the value prop to sync the data.
 *
 * @example change mode
 *
 * ```html
 * <SelectPopover
 *   label="applyPopover"
 *   :modelValue="value"
 *   :options="options"
 *   @update:modelValue="(val) => value = val"
 * />
 *  ```
 *
 * # Apply Mode
 *
 * The "apply" mode does not emit "change". The selection
 * is stored until the user clicks the apply button, or outside or
 * the popover, then "apply" is emitted.
 *
 * If the user clicks "cancel" or presses "escape", the selection
 * is reset to the initial props value.
 *
 * @example apply mode
 *
 * ```html
 * <SelectPopover
 *   label="applyPopover"
 *   :modelValue="value"
 *   :options="options"
 *   mode="apply"
 *   @apply="(val) => value = val"
 * />
 *  ```
 *
 * # Single-Select
 *
 * In single-select mode, the popover closes as soon as a selection
 * is made.
 *
 */
export default defineComponent({
  name: 'SelectPopover',

  components: {
    SearchIcon16,
    SelectItemPlaceholder,
  },

  props: {
    type: {
      type: String as PropType<'multi' | 'single'>,
      default: 'single',
    },

    buttonColor: {
      type: String,
      default: 'grey',
      validator(value: string) {
        return ['grey', 'white', 'ghost'].indexOf(value) !== -1;
      },
    },

    buttonSize: {
      type: String as PropType<'small' | 'medium'>,
      default: 'medium',
    },

    buttonIcon: {
      type: Boolean,
      default: false,
    },

    size: {
      type: String as PropType<'fixed' | 'dynamic'>,
      default: 'dynamic',
    },

    options: {
      type: Array,
      required: true,
    },

    optionGroups: {
      type: Object as PropType<ListGroups>,
      default: () => ({}),
    },

    modelValue: {
      type: [Array, String, Object, Symbol],
      required: true,
    },

    label: {
      type: String,
      required: true,
    },

    /**
     * Renders a search input
     *
     * If function is passed, it is called to get the text for each item when filtering the list via
     * the search input
     */
    search: {
      type: [Boolean, Function] as unknown as PropType<boolean | ((item: any) => string)>,
      default: false,
    },

    error: {
      type: Boolean,
      default: false,
    },

    emptyText: {
      type: String,
      default: '',
    },

    searchEmptyText: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    showActionButtons: {
      type: Boolean,
      default: true,
    },

    mode: {
      type: String as PropType<'change' | 'apply'>,
      default: 'change',
    },

    clickableOption: {
      type: [Boolean, String] as unknown as PropType<boolean | 'custom-control'>,
      default: true,
    },

    menuTitle: {
      type: String,
      required: false,
      default: undefined,
    },

    initiallyHighlightFirstElement: {
      type: Boolean,
      default: true,
    },
  },
  emits: { apply: null, cancel: null, 'update:modelValue': null, done: null, show: null, hide: null },

  data() {
    return {
      query: '',
      open: false,
      currentValue: null,
    };
  },

  computed: {
    filteredOptions(): Array<any> {
      if (!this.query) return this.options;

      const queryLower = toLower(this.query);

      const textGetter = this.search === true ? prop('name') : (this.search as (label: any) => string);

      return filter(({ label }) => toLower(textGetter(label)).indexOf(queryLower) !== -1, this.options);
    },
  },

  methods: {
    onPopoverAutoHide({ reason }: OverlayCloseEvent) {
      if (this.mode === 'apply') {
        if (reason === 'outsideClick') {
          this.apply();
        } else if (reason === 'escapeKey') {
          this.cancel();
        }
      }
    },

    onPopoverHide() {
      this.$refs.SelectionList.resetPositionData();
      this.$refs.SelectionList.resetFocusState();
    },

    onValueChange(value: any) {
      if (this.mode !== 'apply') {
        this.$emit('update:modelValue', value);

        if (this.type === 'single') {
          this.open = false;
        }
      }
    },

    apply() {
      this.$emit('apply', this.currentValue);
    },

    cancel() {
      this.currentValue = this.modelValue;
      this.$emit('cancel');
    },

    dispatchToSelectionList(action) {
      this.$refs.SelectionList[action.method](...action.arguments);
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      /**
       * Sync the internal value and the props.value
       * This is also needed for mode: apply,
       * because the parent might programmatically change the value
       */
      handler(newVal) {
        this.currentValue = newVal;
      },
    },
    open(val) {
      this.$emit(val ? 'show' : 'hide');

      if (val)
        setTimeout(() => {
          this.$refs.input?.focus();
        }, 200);
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';

.select-popover__button {
  width: 100%;
}

.select-popover-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.select-popover__list--size-fixed {
  min-width: 232px;
  max-width: 300px;
}

.select-popover__search {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.select-popover__selection-list .selection-list__body--dense {
  padding: 4px;
  margin: -4px;
  overflow: auto;
}

.select-popover__wrapper {
  padding: 10px;
}

.select-popover__wrapper-no-search {
  padding: 8px;
}

.select-popover__option {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.select-popover__button--error {
  border: 1px solid colors.$input-error !important;
}
</style>
